import { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase/firebase";
import { doc, getDoc } from "firebase/firestore";
import { useDispatch } from "react-redux";
import { loginSuccess, setStudentData, setTeacherData } from "../store/index.js";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getSafeRedirectPath } from "../utils/safeRedirect";
import { isAdminEmail } from "../constants/admins.js";
import { getTeacherAuthRole } from "../utils/teacherStatus";
import styles from "./AuthForm.module.css";
import ForgotPassword from "./ForgotPassword";
import { useTranslation } from "react-i18next";

const Login = () => {
  const { t } = useTranslation();
  const [data, setData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const resolveUser = async (user) => {
    const uid = user.uid;
    const redirectTarget = getSafeRedirectPath(searchParams.get("redirect"));

    if (isAdminEmail(user.email)) {
      dispatch(loginSuccess({ uid, role: "admin" }));
      navigate("/home");
      return;
    }

    const teacherSnap = await getDoc(doc(db, "teachers", uid));
    if (teacherSnap.exists()) {
      const teacherData = teacherSnap.data();
      const teacherRole = getTeacherAuthRole(teacherData);
      dispatch(loginSuccess({ uid, role: teacherRole }));
      dispatch(setTeacherData(teacherData));
      navigate(teacherRole === "teacher" ? redirectTarget : "/home");
      return;
    }

    const studentSnap = await getDoc(doc(db, "students", uid));
    if (studentSnap.exists()) {
      dispatch(loginSuccess({ uid, role: "student" }));
      dispatch(setStudentData(studentSnap.data()));
      navigate(redirectTarget);
      return;
    }

    throw new Error("User role not found");
  };

  const loginHandler = async (e) => {
    e.preventDefault();
    try {
      const cred = await signInWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );
      await resolveUser(cred.user);
    } catch (err) {
      setError(err.message);
    }
  };

  if (showForgotPassword) {
    return (
      <div className={styles.authPage}>
        <div className={styles.authCard}>
          <ForgotPassword onBack={() => setShowForgotPassword(false)} />
        </div>
      </div>
    );
  }

  return (
    <form className={styles.authPage}>
      <div className={styles.authCard}>
        <h2 className={styles.title}>{t("login.title")}</h2>

        <div className={styles.form}>
          <input
            className={styles.input}
            placeholder={t("login.email")}
            onChange={(e) => setData({ ...data, email: e.target.value })}
          />

          <input
            className={styles.input}
            type="password"
            placeholder="Password"
            onChange={(e) => setData({ ...data, password: e.target.value })}
          />

          {error && <div className={styles.error}>{error}</div>}

          <button className={styles.button} onClick={loginHandler}>
            {t("login.loginButton")}
          </button>

          <button
            className={`${styles.button} ${styles.forgPassButton}`}
            type="button"
            onClick={() => setShowForgotPassword(true)}
          >
            {t("login.forgotPassword")}
          </button>
        </div>

        <p className={styles.footerText}>
          {t("login.noAccount")}{" "}
          <span className={styles.link} onClick={() => navigate("/register")}>
            {t("login.register")}
          </span>
        </p>
      </div>
    </form>
  );
};

export default Login;
